import React from 'react';

const VideoCollectionSkeleton = () => {
  // Animation keyframes for shimmer effect
  const shimmerKeyframes = `
    @keyframes shimmer {
      0% {
        background-position: -468px 0;
      }
      100% {
        background-position: 468px 0;
      }
    }
  `;

  const shimmerStyle = {
    background: 'linear-gradient(to right, #f6f7f8 8%, #edeef1 18%, #f6f7f8 33%)',
    backgroundSize: '800px 104px',
    animation: 'shimmer 1.5s ease-in-out infinite',
    borderRadius: '8px',
  };

  const SkeletonCard = () => (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #e8e8e8',
        overflow: 'hidden',
        height: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Thumbnail skeleton */}
      <div
        style={{
          height: '180px',
          background: 'linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Badge skeleton */}
        <div
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '60px',
            height: '20px',
            borderRadius: '12px',
            ...shimmerStyle,
          }}
        />
        
        {/* Play button skeleton */}
        <div
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            backgroundColor: '#e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: '20px',
              height: '20px',
              ...shimmerStyle,
            }}
          />
        </div>
      </div>

      {/* Content skeleton */}
      <div style={{ padding: '20px' }}>
        {/* Title skeleton */}
        <div
          style={{
            width: '80%',
            height: '20px',
            marginBottom: '8px',
            ...shimmerStyle,
          }}
        />
        
        {/* Description skeleton */}
        <div
          style={{
            width: '100%',
            height: '14px',
            marginBottom: '4px',
            ...shimmerStyle,
          }}
        />
        <div
          style={{
            width: '70%',
            height: '14px',
            marginBottom: '16px',
            ...shimmerStyle,
          }}
        />

        {/* Action buttons skeleton */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '16px',
          }}
        >
          <div
            style={{
              width: '80px',
              height: '16px',
              ...shimmerStyle,
            }}
          />
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <div
              style={{
                width: '60px',
                height: '32px',
                borderRadius: '8px',
                ...shimmerStyle,
              }}
            />
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                ...shimmerStyle,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const HeaderSkeleton = () => (
    <div
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '32px',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative circles */}
      <div
        style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '300px',
          height: '300px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
          opacity: 0.3,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-30%',
          left: '-5%',
          width: '200px',
          height: '200px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
          opacity: 0.5,
        }}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <div>
          {/* Title skeleton */}
          <div
            style={{
              width: '300px',
              height: '32px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: '8px',
              marginBottom: '8px',
              ...shimmerStyle,
              background: 'linear-gradient(to right, rgba(255,255,255,0.1) 8%, rgba(255,255,255,0.2) 18%, rgba(255,255,255,0.1) 33%)',
              backgroundSize: '800px 104px',
            }}
          />
          
          {/* Subtitle skeleton */}
          <div
            style={{
              width: '250px',
              height: '16px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              borderRadius: '8px',
              ...shimmerStyle,
              background: 'linear-gradient(to right, rgba(255,255,255,0.1) 8%, rgba(255,255,255,0.2) 18%, rgba(255,255,255,0.1) 33%)',
              backgroundSize: '800px 104px',
            }}
          />
        </div>

        {/* Button skeleton */}
        <div
          style={{
            width: '160px',
            height: '48px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            borderRadius: '16px',
            ...shimmerStyle,
            background: 'linear-gradient(to right, rgba(255,255,255,0.1) 8%, rgba(255,255,255,0.2) 18%, rgba(255,255,255,0.1) 33%)',
            backgroundSize: '800px 104px',
          }}
        />
      </div>

      {/* Info cards skeleton */}
      <div
        className="row"
        style={{ position: 'relative', zIndex: 2 }}
      >
        {[1, 2].map((i) => (
          <div key={i} className="col-md-6">
            <div
              style={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderRadius: '16px',
                padding: '20px',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div
                    style={{
                      width: '40px',
                      height: '28px',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      borderRadius: '4px',
                      marginBottom: '4px',
                      ...shimmerStyle,
                      background: 'linear-gradient(to right, rgba(255,255,255,0.1) 8%, rgba(255,255,255,0.2) 18%, rgba(255,255,255,0.1) 33%)',
                      backgroundSize: '800px 104px',
                    }}
                  />
                  <div
                    style={{
                      width: '80px',
                      height: '14px',
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      borderRadius: '4px',
                      ...shimmerStyle,
                      background: 'linear-gradient(to right, rgba(255,255,255,0.1) 8%, rgba(255,255,255,0.2) 18%, rgba(255,255,255,0.1) 33%)',
                      backgroundSize: '800px 104px',
                    }}
                  />
                </div>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '4px',
                    ...shimmerStyle,
                    background: 'linear-gradient(to right, rgba(255,255,255,0.1) 8%, rgba(255,255,255,0.2) 18%, rgba(255,255,255,0.1) 33%)',
                    backgroundSize: '800px 104px',
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <style>
        {shimmerKeyframes}
      </style>
      
      <div
        style={{
          overflow: 'auto',
          height: '90vh',
          backgroundColor: '#f8f9fa',
          padding: '0 15px',
        }}
      >
        {/* Header Skeleton */}
        <HeaderSkeleton />

        {/* Collections Grid Skeleton */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '1.5rem',
            padding: '0 15px',
          }}
        >
          {/* Generate 6 skeleton cards */}
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
      </div>
    </>
  );
};

export default VideoCollectionSkeleton;